
import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, Badge, Spinner, IconButton, Collapse } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import useServerApi from 'hooks/useServerApi';
const LIVE_CALLS_URL = '/rest_endpoints/call_history/live-calls';

const Conversation = ({ transcript, expanded }) => {
    if (!Array.isArray(transcript) || transcript.length === 0) return null;
    const shownMessages = expanded ? transcript : transcript.slice(-5);
    return (
        <Box mt={2} maxH={expanded ? '200px' : 'auto'} overflowY={expanded ? 'auto' : 'visible'} borderRadius="md" bg="gray.50" p={2}>
            {shownMessages.map((msg, i) => (
                <Flex key={i} align="flex-start" mb={1}>
                    <Text fontWeight="semibold" fontSize="xs" color={msg.role === 'user' ? 'blue.600' : msg.role === 'assistant' ? 'green.600' : 'gray.600'} mr={2} minW="60px">
                        {msg.role.charAt(0).toUpperCase() + msg.role.slice(1)}:
                    </Text>
                    <Text fontSize="sm" color="gray.800">{msg.message}</Text>
                </Flex>
            ))}
            {!expanded && transcript.length > 5 && (
                <Text fontSize="xs" color="gray.400" textAlign="center">...showing last 5 messages</Text>
            )}
        </Box>
    );
};

const LiveCalls = () => {
    const [liveCalls, setLiveCalls] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [expandedIdx, setExpandedIdx] = useState({});
    const [{ post }] = useServerApi();

    useEffect(() => {
        const fetchLiveCalls = async () => {
            try {
                const res = await post(LIVE_CALLS_URL, { method: 'POST' });
                const data = Array.isArray(res) && Array.isArray(res[1]) ? res[1] : [];
                setLiveCalls(data);
                if (data.length > 0) {
                    setTimeout(fetchLiveCalls, 1000);
                    return;
                }
                setTimeout(fetchLiveCalls, 5000);
            } catch (e) {
                setLiveCalls([]);
                setTimeout(fetchLiveCalls, 5000);
            } finally {
                setLoaded(true);
            }
        };

        fetchLiveCalls();
    }, [post]);

    const toggleExpand = idx => {
        setExpandedIdx(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    if (liveCalls.length === 0) {
        return null;
    }
    return (
        <Box mb={6} p={4} bg="gray.50" borderRadius="md" boxShadow="sm">
            <Flex align="center" justify="space-between" mb={2}>
                <Text fontWeight="bold">Current Live Calls</Text>
                <Badge colorScheme="blue" fontSize="md">{liveCalls.length}</Badge>
            </Flex>
            {!loaded ? (
                <Spinner size="sm" />
            ) : liveCalls.length === 0 ? (
                <Text color="gray.500">No live calls</Text>
            ) : (
                <Flex gap={4} wrap="wrap">
                    {liveCalls.map((call, idx) => {
                        const transcript = call.live_transcript || [];
                        const expanded = !!expandedIdx[idx];
                        return (
                            <Box key={idx} p={3} borderRadius="md" bg="white" boxShadow="xs" minW="250px" maxW="350px">
                                <Flex align="center" justify="space-between" mb={1}>
                                    <Box>
                                        <Text fontWeight="bold">{call.caller_name !== '' ? call.caller_name : call.caller_number}</Text>
                                        {call.caller_name !== '' && (<Text fontSize="sm" color="gray.600">{call.caller_number}</Text>)}
                                    </Box>
                                    {transcript.length > 0 && (
                                        <IconButton
                                            size="sm"
                                            aria-label={expanded ? 'Collapse conversation' : 'Expand conversation'}
                                            icon={expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                            onClick={() => toggleExpand(idx)}
                                            variant="ghost"
                                        />
                                    )}
                                </Flex>
                                <Text fontSize="xs" color="gray.500">Duration: {call.duration || (call.status === 'Ringing' ? <span style={{ color: '#f6ad55' }}>Ringing</span> : 'N/A')}</Text>
                                <Text fontSize="xs" color="orange.500">{call.status}</Text>
                                {transcript.length > 0 && (
                                    <Collapse in={expanded} animateOpacity={false} startingHeight={0}>
                                        <Conversation transcript={transcript} expanded={expanded} />
                                    </Collapse>
                                )}
                                {transcript.length > 0 && !expanded && (
                                    <Conversation transcript={transcript} expanded={false} />
                                )}
                            </Box>
                        );
                    })}
                </Flex>
            )}
        </Box>
    );
};

export default LiveCalls;
